// Form-field-sized select with the same glass popover as the project
// switcher. Trigger matches the height + border of the text inputs in
// the project Settings page, so it sits inline with them visually.
//
// Wraps base-ui's Select. The visual treatment is the only thing that
// differs from a plain `Select.Root`; the API surface is the same
// (Root → Trigger → Portal → Positioner → Popup → Item).

import { Select } from "@base-ui-components/react/select";
import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

export interface GlassSelectItemData {
	value: string;
	label: string;
	hint?: string;
}

interface GlassSelectProps {
	value: string;
	onValueChange: (v: string) => void;
	placeholder?: string;
	disabled?: boolean;
	mono?: boolean;
	/**
	 * Preferred shape: array of items the select can both render in the
	 * popup AND look up to display the selected label in the trigger.
	 * When provided, `children` is ignored.
	 */
	items?: GlassSelectItemData[];
	/**
	 * Legacy / custom rendering path. Used when `items` is not supplied
	 * (e.g. for selects with grouped or otherwise non-uniform items).
	 * Note: the trigger will fall back to the raw value when only
	 * children are used, since GlassSelect can't introspect them. Use
	 * `items` for the common case.
	 */
	children?: ReactNode;
}

export function GlassSelect({
	value,
	onValueChange,
	placeholder,
	disabled,
	mono,
	items,
	children,
}: GlassSelectProps) {
	const selected = items?.find((it) => it.value === value) ?? null;
	return (
		<Select.Root
			value={value}
			onValueChange={(v) => onValueChange((v as string | null) ?? "")}
			disabled={disabled}
		>
			<Select.Trigger
				className={`w-full h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none flex items-center justify-between gap-2 transition-colors data-[popup-open]:border-[var(--color-border-hi)] disabled:opacity-50 disabled:cursor-not-allowed ${
					mono ? "mono" : ""
				}`}
			>
				<span className="truncate text-left flex-1">
					{selected ? (
						selected.label
					) : value && !items ? (
						<Select.Value />
					) : (
						<span className="text-[var(--color-text-dim)]">
							{placeholder ?? "Select…"}
						</span>
					)}
				</span>
				<ChevronDown className="w-3.5 h-3.5 text-[var(--color-text-dim)] shrink-0" />
			</Select.Trigger>
			<Select.Portal>
				<Select.Positioner sideOffset={6} align="start" className="z-50">
					<Select.Popup
						className="rounded-[12px] py-1 min-w-[var(--anchor-width)] max-h-[60vh] overflow-y-auto"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
						}}
					>
						{items
							? items.map((it) => (
									<GlassSelectItem
										key={it.value}
										value={it.value}
										label={it.label}
										hint={it.hint}
										mono={mono}
									/>
								))
							: children}
					</Select.Popup>
				</Select.Positioner>
			</Select.Portal>
		</Select.Root>
	);
}

interface GlassSelectItemProps {
	value: string;
	label: string;
	hint?: string; // smaller right-aligned text (e.g. "private", "organization")
	mono?: boolean;
	children?: ReactNode; // custom rendering — overrides label/hint
}

export function GlassSelectItem({
	value,
	label,
	hint,
	mono,
	children,
}: GlassSelectItemProps) {
	return (
		<Select.Item
			value={value}
			className="flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06] data-[selected]:bg-white/[0.04]"
		>
			{children ?? (
				<>
					<Select.ItemText
						className={`truncate flex-1 ${mono ? "mono" : ""}`}
					>
						{label}
					</Select.ItemText>
					{hint && (
						<span className="text-[11px] text-[var(--color-text-dim)] shrink-0">
							{hint}
						</span>
					)}
				</>
			)}
			<Select.ItemIndicator className="shrink-0">
				<Check className="w-3.5 h-3.5 text-[var(--color-text)]" />
			</Select.ItemIndicator>
		</Select.Item>
	);
}

